import { StatusBadge } from '@/features/analytics/invite-links/ui/InviteLinksTable/shared/cells/StatusCell/StatusBadge'
import Act from '@/features/analytics/invite-links/ui/Marking/components/Act/Act'
import { Contract } from '@/features/analytics/invite-links/ui/Marking/components/Contract/Contract'
import Invoice from '@/features/analytics/invite-links/ui/Marking/components/Invoice'
import LinkedLinks from '@/features/analytics/invite-links/ui/Marking/components/LinkedLinks'
import Platform from '@/features/analytics/invite-links/ui/Marking/components/Platform'
import { Publisher } from '@/features/analytics/invite-links/ui/Marking/components/Publisher'
import { PublishOptions } from '@/features/analytics/invite-links/ui/Marking/components/PublishOptions/PublishOptions'
import { useInviteLinksStore } from '@/features/analytics/model'
import { AnalyticsTableRowType } from '@/features/analytics/types'
import { useGetActs, useGetContracts } from '@/shared/api/google-drive'
import {
    useGetAgencyContracts,
    useGetVkOrdHistory,
    UseSavedErid,
} from '@/shared/api/ord'
import { CloseIcon } from '@/shared/assets'
import { OrdAdditionalDataAvailable } from '@/shared/hooks'
import {
    Dialog,
    DialogHeader,
    DialogSimpleContent,
    DialogTitle,
    DialogTrigger,
    ShadcnButton,
} from '@/shared/ui'
import { ORDMarkingFormData } from '@synapex/prisma/client'
import {
    WorkspaceWithLinkDescriptor,
    workspaceWithLinkMethods,
} from '@synapex/shared2/entities'
import { ActUnion } from '@synapex/shared2/gateway-rest-api'
import { ReactNode, useMemo, useState } from 'react'
import { Creative } from './components/Creative'
import { EridLink } from './components/EridLink'
import { OrdStatus } from './components/OrdStatus'
import PublishedPost from './components/PublishedPost'

export type MarkingModalProps = {
    groupId: number
    analyticsTableRowType: AnalyticsTableRowType
    handleSelectedAdminContactIdChange: (
        editingRowId: number,
        selectedAdminContactId: number | null
    ) => void

    ordAdditionalData: OrdAdditionalDataAvailable
    markingFormData: ORDMarkingFormData
    savedEridKktu: UseSavedErid
    trigger?: ReactNode
} & WorkspaceWithLinkDescriptor

export const MarkingModal: React.FC<MarkingModalProps> = ({
    groupId,
    analyticsTableRowType,
    handleSelectedAdminContactIdChange,
    ordAdditionalData,
    markingFormData,
    trigger,
    savedEridKktu,
    ...props
}) => {
    const wwld = workspaceWithLinkMethods.pickWwld(props)
    const clearErrors = useInviteLinksStore((state) => state.clearErrors)

    const [openLinkedLinks, setOpenLinkedLinks] = useState(false)
    const [openMarkingModal, setOpenMarkingModal] = useState(false)

    const { data: contracts } = useGetContracts(
        {
            ...wwld,
            contragentDistributorId: markingFormData.ord_contragent_id!,
        },
        {
            enabled: !!markingFormData.ord_contragent_id,
        }
    )

    const { data: agencyContracts } = useGetAgencyContracts({
        ...wwld,
    })

    const { data: acts } = useGetActs(
        {
            ...wwld,
            contragentDistributorId: markingFormData.ord_contragent_id!,
        },
        {
            enabled: !!markingFormData.ord_contragent_id,
        }
    )

    const { data: vkOrdHistory } = useGetVkOrdHistory(wwld, {
        enabled: openMarkingModal,
        retry: false, // Не повторять запрос при ошибке
        retryOnMount: false, // Не повторять при монтировании
        refetchOnWindowFocus: false, // Не обновлять при фокусе окна
        // Тихая обработка ошибок - компоненты работают с пустым массивом при ошибке
    })

    function openLinkedLinksModal() {
        setOpenLinkedLinks(true)
    }

    const draftLinkedContract = useMemo(() => {
        return contracts?.find(
            (contract) =>
                (contract.type === 'default' &&
                    contract.documentRecordId ===
                        markingFormData.draft_contract_id) ||
                (contract.type === 'custom' &&
                    contract.custom_contract_id ===
                        markingFormData.custom_contract_id)
        )
    }, [
        contracts,
        markingFormData.draft_contract_id,
        markingFormData.custom_contract_id,
    ])

    const draftAct = useMemo(() => {
        if (!acts) {
            return undefined
        }

        const defaultAct = acts.find(
            (act): act is Extract<ActUnion, { type: 'default' }> =>
                act.type === 'default' &&
                act.documentRecordId === markingFormData.draft_act_id
        )

        if (defaultAct) {
            return defaultAct
        }

        return acts.find(
            (act): act is Extract<ActUnion, { type: 'custom' }> =>
                act.type === 'custom' &&
                act.custom_act_id === markingFormData.custom_act_id
        )
    }, [acts, markingFormData.custom_act_id, markingFormData.draft_act_id])

    const linkedLinksCount =
        (draftLinkedContract?.type === 'default'
            ? draftLinkedContract._count.draft_ord_marking_form_data_contract
            : draftLinkedContract?._count.ord_marking_form_data) ?? 0

    // if link deleted_at is not null, then isLinkDeleted is true
    const isLinkDeleted = !!analyticsTableRowType.publisher?.link_deleted_at

    const workspaceHasInWorkAgencyContract = !!agencyContracts?.some(
        (contract) => contract.status === 'in_work'
    )

    const statisticsStatus = useMemo(() => {
        if (
            vkOrdHistory?.some(
                (history) =>
                    history.type === 'statistics' &&
                    history.lastUpdateStatus === 'verified' &&
                    history.hasEndStatistics
            )
        ) {
            return true
        }
        return isLinkDeleted ? 'error' : false
    }, [isLinkDeleted, vkOrdHistory])

    return (
        <Dialog
            open={openMarkingModal}
            onOpenChange={(isOpen) => {
                setOpenMarkingModal(isOpen)
                clearErrors()
            }}
            modal={false}
        >
            <DialogTrigger asChild>{trigger}</DialogTrigger>

            {openMarkingModal && (
                <DialogSimpleContent
                    aria-describedby={undefined}
                    className="left-auto right-0 top-0 flex h-screen w-full max-w-[631px] translate-x-0 translate-y-0 flex-row gap-0 bg-white p-0 shadow-[0px_4px_24px_0px_rgba(0,0,0,0.15)]"
                    outerChildren={
                        <div className="pointer-events-none absolute right-0 top-0 z-[50] h-screen w-[143px] bg-blue-600/5" />
                    }
                >
                    <LinkedLinks
                        {...wwld}
                        open={openLinkedLinks}
                        setOpen={setOpenLinkedLinks}
                        draftContract={draftLinkedContract}
                        draftAct={draftAct}
                        markingFormData={markingFormData}
                    />

                    <div className="flex h-full flex-col">
                        <DialogHeader className="justify-center p-5 pb-2 text-base font-semibold">
                            <DialogTitle className="flex items-center">
                                <div className="flex items-center gap-3">
                                    <StatusBadge
                                        className={'w-[127px]'}
                                        value={analyticsTableRowType.status}
                                    />
                                    <span>Маркировка и документооборот</span>
                                </div>
                                <ShadcnButton
                                    className="p-0 px-4"
                                    onClick={() => setOpenMarkingModal(false)}
                                >
                                    <img
                                        src={CloseIcon}
                                        style={{
                                            filter: 'invert(50%)', // TODO: fix to normal color
                                        }}
                                    />
                                </ShadcnButton>
                            </DialogTitle>
                        </DialogHeader>
                        <div className="flex min-h-0 w-[611px] flex-1 flex-row items-stretch">
                            <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-5 pr-0 pt-2">
                                <Platform
                                    {...{
                                        groupId,
                                        analyticsTableRowType,
                                        handleSelectedAdminContactIdChange,
                                    }}
                                />

                                <PublishOptions
                                    {...wwld}
                                    {...{
                                        markingFormData,
                                    }}
                                />

                                <Publisher {...wwld} {...{ markingFormData }} />

                                <Contract
                                    {...wwld}
                                    {...{
                                        markingFormData,
                                        openLinkedLinksModal,
                                        contracts,
                                        linkedLinksCount,
                                        isAgency:
                                            ordAdditionalData.currentLinkType ===
                                            'agency',
                                        workspaceHasInWorkAgencyContract,
                                    }}
                                />

                                <Invoice
                                    {...wwld}
                                    {...{
                                        analyticsTableRowType,
                                        openLinkedLinksModal,
                                        markingFormData,
                                        linkedLinksCount,
                                        isLinkDeleted,
                                    }}
                                />

                                <Creative
                                    {...wwld}
                                    {...savedEridKktu}
                                    latestKktu={ordAdditionalData.lastKktu}
                                    flagValue={markingFormData.creative_flag}
                                    draftContractId={
                                        markingFormData.draft_contract_id ??
                                        undefined
                                    }
                                    customContractId={
                                        markingFormData.custom_contract_id ??
                                        undefined
                                    }
                                    isLinkDeleted={isLinkDeleted}
                                    groupId={groupId}
                                    tableRowData={analyticsTableRowType}
                                    isContractConfirmed={
                                        markingFormData.contract_flag ||
                                        markingFormData.contract_edo_flag
                                    }
                                />

                                <EridLink
                                    {...wwld}
                                    groupId={groupId}
                                    adOrdContragentId={
                                        markingFormData.ord_contragent_id ??
                                        ordAdditionalData.adOrdContragentId
                                    }
                                    ordAdditionalData={ordAdditionalData}
                                    markingFormData={markingFormData}
                                    savedErid={savedEridKktu.savedErid}
                                    tableRowData={analyticsTableRowType}
                                />

                                <PublishedPost
                                    analyticsTableRowType={
                                        analyticsTableRowType
                                    }
                                    markingFormData={markingFormData}
                                    isLinkDeleted={isLinkDeleted}
                                />

                                <Act
                                    {...wwld}
                                    vkOrdHistory={vkOrdHistory ?? []}
                                    markingFormData={markingFormData}
                                    analyticsTableRowType={
                                        analyticsTableRowType
                                    }
                                    openLinkedLinksModal={openLinkedLinksModal}
                                    acts={acts}
                                    linkedLinksCount={linkedLinksCount}
                                    contract={draftLinkedContract}
                                />

                                <div className="mt-auto">
                                    <OrdStatus
                                        vkOrdHistory={vkOrdHistory ?? []}
                                        flagStatus={statisticsStatus}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogSimpleContent>
            )}
        </Dialog>
    )
}
